import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChargeService } from '../../../services/charge.service';
import { Charge } from '../../../models/charge.model';
import { HouseFlat } from '../../../models/house-flat.model';
import { HouseFlatModalComponent } from '../../../components/house-flat-modal/house-flat-modal.component';
import { HouseFlatService } from '../../../services/house-flat.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PaymentModalComponent } from '../../../components/payment-modal/payment-modal.component';
import { StreetBuilding } from '../../../models/street-building.model';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { NeighborhoodService } from '../../../services/neighborhood.service';
import { PaymentService } from '../../../services/payment.service';
import { MovementService } from '../../../services/movement.service';
import { IMovement } from '../../../models/movement.model';

/**
 * Estructura paralela ligera para el filtrado.
 * `streetBuilding` y cada elemento de `housesFlats` son SIEMPRE las
 * instancias originales de `charge.streetsBuildings`, nunca copias:
 * `update()` muta `houseFlat.status` y los modales reciben el
 * `streetBuilding` original por referencia.
 */
export interface StreetBuildingGroup {
  streetBuilding: StreetBuilding;
  housesFlats: HouseFlat[];
}

@Component({
  selector: 'app-payment-page',
  templateUrl: './payment-page.component.html',
  styleUrl: './payment-page.component.scss'
})
export class PaymentPageComponent {

  public neighborhoodId: string = '';
  public chargeId: string = '';
  public charge?: Charge;
  public isLoading: boolean = true;

  public housesFlatsSearch: string = '';
  public streetsBuildingsFiltered: StreetBuildingGroup[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    public chargeService: ChargeService,
    public houseFlatService: HouseFlatService,
    private modalService: NgbModal,
    public neighborhoodService: NeighborhoodService,
    private paymentService: PaymentService,
    public movementService: MovementService,
  ) {

    this.activatedRoute.params.subscribe(
      async params => {

        // console.log(params);

        this.isLoading = true;
        this.neighborhoodId = params['neighborhoodId'];
        this.chargeId = params['paymentId'];
        this.housesFlatsSearch = '';

        try {
          this.charge = await this.chargeService.get(
            this.neighborhoodId!,
            this.chargeId!
          );

          this.applyHousesFlatsFilter();
        } finally {
          this.isLoading = false;
        }
      }
    );

  }

  /*
  ██████╗  ██████╗ ██╗    ██╗███╗   ██╗██╗      ██████╗  █████╗ ██████╗ ██████╗ ███████╗██████╗  ██████╗ ██████╗ ████████╗
  ██╔══██╗██╔═══██╗██║    ██║████╗  ██║██║     ██╔═══██╗██╔══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗██╔═══██╗██╔══██╗╚══██╔══╝
  ██║  ██║██║   ██║██║ █╗ ██║██╔██╗ ██║██║     ██║   ██║███████║██║  ██║██████╔╝█████╗  ██████╔╝██║   ██║██████╔╝   ██║
  ██║  ██║██║   ██║██║███╗██║██║╚██╗██║██║     ██║   ██║██╔══██║██║  ██║██╔══██╗██╔══╝  ██╔═══╝ ██║   ██║██╔══██╗   ██║
  ██████╔╝╚██████╔╝╚███╔███╔╝██║ ╚████║███████╗╚██████╔╝██║  ██║██████╔╝██║  ██║███████╗██║     ╚██████╔╝██║  ██║   ██║
  ╚═════╝  ╚═════╝  ╚══╝╚══╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═╝  ╚═╝╚══════╝╚═╝      ╚═════╝ ╚═╝  ╚═╝   ╚═╝

  */
  downloadReport() {

    const data: any[] = [];
    const mergeRanges: any[] = [];

    let currentIndex = 0, 
      noPaid = 0, 
      validating = 0, 
      paid = 0, 
      noPaidAmount = 0, 
      validatingAmount = 0, 
      paidAmount = 0;

    this.charge!.streetsBuildings.forEach(
      (streetBuilding, index) => {
        data.push([
          streetBuilding.name
        ]);

        mergeRanges.push({
          s: { r: currentIndex, c: 0 }, // Celda de inicio (row, column)
          e: { r: currentIndex, c: 5 }  // Celda de fin (row, column) - 6 columnas combinadas
        });

        currentIndex++;
        data.push([
          'NÚMERO',
          'NOMBRE',
          'TELÉFONO',
          'NOTAS',
          'MONTO',
          'ESTATUS DE PAGO',
          'NOTAS DEL PAGO',
          'EXTRA'
        ]);

        streetBuilding.housesFlats.forEach(
          (houseFlat, jindex) => {
            currentIndex++;

            const record = houseFlat.payment ?? houseFlat.movement;
            const recordStatus = record?.status ?? null;
            const recordAmount = houseFlat.payment?.charge ?? houseFlat.movement?.amount ?? 0;
            const recordNotes = houseFlat.payment?.notes ?? houseFlat.movement?.notes ?? '';

            if(record) {

              if(recordStatus === true) {
                paid++;
                paidAmount += recordAmount;
              } else if(recordStatus === false) {
                validating++;
                validatingAmount += recordAmount;
              } else {
                noPaid++;
                noPaidAmount += recordAmount;
              }

            }

            const statusLabel = recordStatus === true
              ? 'Pagado'
              : recordStatus === false
                ? 'No pagado'
                : 'Pendiente';

            data.push([
              houseFlat.number,
              houseFlat.fullName,
              houseFlat.phone,
              houseFlat.notes,
              recordAmount || undefined,
              statusLabel,
              recordNotes,
              ''
            ]);
          }
        );

        currentIndex++;
      }
    );

    console.log(data);

    // Crear una hoja de Excel
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    worksheet['!merges'] = mergeRanges;

    // Calcular el ancho máximo de cada columna
    const columnWidths = data[1].map((_: any, colIndex: string | number) => {
      const maxLength = data.reduce((max, row) => {
        const cell = row[colIndex];
        const cellLength = cell ? cell.toString().length : 0;
        return Math.max(max, cellLength);
      }, 0);
      return { wch: maxLength + 2 }; // Ajuste adicional para margen
    });

    // Asignar los anchos de columna al worksheet
    worksheet['!cols'] = columnWidths;

    // Crear un libro de trabajo y agregar la hoja
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook, 
      worksheet,
      moment(this.charge!.createdAt).add(
        this.neighborhoodService.currentNeighborhood?.timeZone, 'hours'
      ).format('MMMM YYYY')
    );

    const dataResume = [
      ['Estatus', 'No. de predios', 'Monto total'],
      ['No pagados', noPaid, noPaidAmount],
      ['Validando', validating, validatingAmount],
      ['Pagados', paid, paidAmount],
    ]

    const worksheetResume = XLSX.utils.aoa_to_sheet(dataResume);
    XLSX.utils.book_append_sheet(
      workbook, 
      worksheetResume,
      'Resumen'
    );

    // Exportar el archivo XLSX
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Crear un Blob para la descarga
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

    // Crear un enlace de descarga
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);

    const shorDate = moment(this.charge!.createdAt).add(
      this.neighborhoodService.currentNeighborhood?.timeZone, 'hours'
    ).format('YYYYMM')

    // Asignar el nombre del archivo
    link.download = `Reporte de mantenimeinto ${this.neighborhoodService.currentNeighborhood?.name} ${shorDate}.xlsx`;

    // Disparar la descarga
    link.click();

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    houseFlat: HouseFlat
  ) {
    const flag = await this.houseFlatService.update(
      this.neighborhoodId,
      houseFlat
    );

    if(!flag) {
      houseFlat.status = !houseFlat.status;
    }
  }

  /*
    ██████╗ ██████╗ ███████╗███╗   ██╗██╗  ██╗ ██████╗ ██╗   ██╗███████╗███████╗███████╗██╗      █████╗ ████████╗
  ██╔═══██╗██╔══██╗██╔════╝████╗  ██║██║  ██║██╔═══██╗██║   ██║██╔════╝██╔════╝██╔════╝██║     ██╔══██╗╚══██╔══╝
  ██║   ██║██████╔╝█████╗  ██╔██╗ ██║███████║██║   ██║██║   ██║███████╗█████╗  █████╗  ██║     ███████║   ██║
  ██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║██╔══██║██║   ██║██║   ██║╚════██║██╔══╝  ██╔══╝  ██║     ██╔══██║   ██║
  ╚██████╔╝██║     ███████╗██║ ╚████║██║  ██║╚██████╔╝╚██████╔╝███████║███████╗██║     ███████╗██║  ██║   ██║
    ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝   ╚═╝

  */
  openHouseFlat(
    houseFlat: HouseFlat,
  ) {
    const modalRef = this.modalService.open(
      HouseFlatModalComponent
    );

    modalRef.componentInstance.houseFlat = houseFlat;
    modalRef.componentInstance.neighborhoodId = this.neighborhoodId;
    modalRef.componentInstance.streetBuildingId = houseFlat.streetBuildingId;

    modalRef.closed.subscribe(
      async () => {
    
        this.charge = await this.chargeService.get(
          this.neighborhoodId!,
          this.chargeId!
        );

        this.applyHousesFlatsFilter();

      }
    );

  }

  /*
   ██████╗ ██████╗ ███████╗███╗   ██╗██████╗  █████╗ ██╗   ██╗███╗   ███╗███████╗███╗   ██╗████████╗
  ██╔═══██╗██╔══██╗██╔════╝████╗  ██║██╔══██╗██╔══██╗╚██╗ ██╔╝████╗ ████║██╔════╝████╗  ██║╚══██╔══╝
  ██║   ██║██████╔╝█████╗  ██╔██╗ ██║██████╔╝███████║ ╚████╔╝ ██╔████╔██║█████╗  ██╔██╗ ██║   ██║
  ██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║██╔═══╝ ██╔══██║  ╚██╔╝  ██║╚██╔╝██║██╔══╝  ██║╚██╗██║   ██║
  ╚██████╔╝██║     ███████╗██║ ╚████║██║     ██║  ██║   ██║   ██║ ╚═╝ ██║███████╗██║ ╚████║   ██║
   ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝╚═╝     ╚═╝  ╚═╝   ╚═╝   ╚═╝     ╚═╝╚══════╝╚═╝  ╚═══╝   ╚═╝

  */
  openPayment(
    streetBuilding: StreetBuilding,
    houseFlat: HouseFlat
  ) {

    const modalRef = this.modalService.open(
      PaymentModalComponent
    );

    modalRef.componentInstance.streetBuilding = streetBuilding;
    modalRef.componentInstance.houseFlat = houseFlat;
    modalRef.componentInstance.neighborhoodId = this.neighborhoodId;

    modalRef.closed.subscribe(
      async () => {

        this.charge = await this.chargeService.get(
          this.neighborhoodId!,
          this.chargeId!
        );

        this.applyHousesFlatsFilter();

      }
    )

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗    ███████╗████████╗ █████╗ ████████╗██╗   ██╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝    ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██║   ██║██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗      ███████╗   ██║   ███████║   ██║   ██║   ██║███████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝      ╚════██║   ██║   ██╔══██║   ██║   ██║   ██║╚════██║
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗    ███████║   ██║   ██║  ██║   ██║   ╚██████╔╝███████║
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝    ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝    ╚═════╝ ╚══════╝

  */
  async updateStatus(
    streetBuilding: StreetBuilding,
    houseFlat: HouseFlat,
    newStatus: boolean | null
  ): Promise<void> {

    // Si no hay payment, redirigir al modal (no hay paymentId para hacer PUT)
    if (!houseFlat.payment) {
      this.openPayment(streetBuilding, houseFlat);
      return;
    }

    // Evitar llamada si el status no cambió
    const currentStatus = houseFlat.payment.status ?? null;
    if (currentStatus === newStatus) return;

    const flag = await this.paymentService.update(
      this.neighborhoodId,
      houseFlat,
      {
        charge: houseFlat.payment.charge,
        status: newStatus,
        notes: houseFlat.payment.notes,
      }
    );

    if (flag) {
      this.charge = await this.chargeService.get(
        this.neighborhoodId!,
        this.chargeId!
      );

      this.applyHousesFlatsFilter();
    }

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗    ███╗   ███╗ ██████╗ ██╗   ██╗███████╗███╗   ███╗███████╗███╗   ██╗████████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝    ████╗ ████║██╔═══██╗██║   ██║██╔════╝████╗ ████║██╔════╝████╗  ██║╚══██╔══╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗      ██╔████╔██║██║   ██║██║   ██║█████╗  ██╔████╔██║█████╗  ██╔██╗ ██║   ██║
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝      ██║╚██╔╝██║██║   ██║╚██╗ ██╔╝██╔══╝  ██║╚██╔╝██║██╔══╝  ██║╚██╗██║   ██║
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗    ██║ ╚═╝ ██║╚██████╔╝ ╚████╔╝ ███████╗██║ ╚═╝ ██║███████╗██║ ╚████║   ██║
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝    ╚═╝     ╚═╝ ╚═════╝   ╚═══╝  ╚══════╝╚═╝     ╚═╝╚══════╝╚═╝  ╚═══╝   ╚═╝

   ███████╗████████╗ █████╗ ████████╗██╗   ██╗███████╗
   ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██║   ██║██╔════╝
   ███████╗   ██║   ███████║   ██║   ██║   ██║███████╗
   ╚════██║   ██║   ██╔══██║   ██║   ██║   ██║╚════██║
   ███████║   ██║   ██║  ██║   ██║   ╚██████╔╝███████║
   ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝    ╚═════╝ ╚══════╝

  */
  async updateMovementStatus(
    houseFlat: HouseFlat,
    newStatus: boolean | null
  ): Promise<void> {
    if (!houseFlat.movement) return;

    const currentStatus = houseFlat.movement.status ?? null;
    if (currentStatus === newStatus) return;

    const updatedMovement: IMovement = {
      movementId: houseFlat.movement.movementId,
      neighborhoodId: houseFlat.movement.neighborhoodId,
      houseFlatId: houseFlat.movement.houseFlatId,
      streetBuildingId: houseFlat.movement.streetBuildingId,
      description: houseFlat.movement.description,
      notes: houseFlat.movement.notes,
      movementType: houseFlat.movement.movementType,
      status: newStatus,
      amount: houseFlat.movement.amount,
      creditApplied: houseFlat.movement.creditApplied,
      createdAt: houseFlat.movement.createdAt instanceof Date
        ? houseFlat.movement.createdAt.toISOString()
        : houseFlat.movement.createdAt as unknown as string,
      updatedAt: houseFlat.movement.updatedAt instanceof Date
        ? houseFlat.movement.updatedAt.toISOString()
        : houseFlat.movement.updatedAt as unknown as string,
    };

    const flag = await this.movementService.update(updatedMovement);

    if (flag) {
      this.charge = await this.chargeService.get(
        this.neighborhoodId!,
        this.chargeId!
      );

      this.applyHousesFlatsFilter();
    }
  }

  /*
  ███████╗██╗██╗     ████████╗███████╗██████╗
  ██╔════╝██║██║     ╚══██╔══╝██╔════╝██╔══██╗
  █████╗  ██║██║        ██║   █████╗  ██████╔╝
  ██╔══╝  ██║██║        ██║   ██╔══╝  ██╔══██╗
  ██║     ██║███████╗   ██║   ███████╗██║  ██║
  ╚═╝     ╚═╝╚══════╝   ╚═╝   ╚══════╝╚═╝  ╚═╝

  */
  // region applyHousesFlatsFilter
  applyHousesFlatsFilter() {

    const streetsBuildings = this.charge?.streetsBuildings ?? [];

    const tokens = this.buildHousesFlatsSearchTokens();

    if(tokens.length === 0) {

      this.streetsBuildingsFiltered = streetsBuildings.map(
        (streetBuilding) => ({
          streetBuilding: streetBuilding,
          housesFlats: streetBuilding.housesFlats
        })
      );

      return;

    }

    const groups: StreetBuildingGroup[] = [];

    streetsBuildings.forEach(
      (streetBuilding) => {

        const streetName = streetBuilding.name ?? '';

        const streetText = this.normalizeSearchText(streetName);

        const matchesStreet = tokens.every(
          (token) => streetText.includes(token)
        );

        if(matchesStreet) {

          groups.push({
            streetBuilding: streetBuilding,
            housesFlats: streetBuilding.housesFlats
          });

          return;

        }

        const housesFlats = streetBuilding.housesFlats.filter(
          (houseFlat) => this.matchesHouseFlatSearch(
            houseFlat,
            streetName,
            tokens
          )
        );

        if(housesFlats.length > 0) {

          groups.push({
            streetBuilding: streetBuilding,
            housesFlats: housesFlats
          });

        }

      }
    );

    this.streetsBuildingsFiltered = groups;

  }

  /*
  ███╗   ██╗ ██████╗ ██████╗ ███╗   ███╗ █████╗ ██╗     ██╗███████╗███████╗
  ████╗  ██║██╔═══██╗██╔══██╗████╗ ████║██╔══██╗██║     ██║╚══███╔╝██╔════╝
  ██╔██╗ ██║██║   ██║██████╔╝██╔████╔██║███████║██║     ██║  ███╔╝ █████╗
  ██║╚██╗██║██║   ██║██╔══██╗██║╚██╔╝██║██╔══██║██║     ██║ ███╔╝  ██╔══╝
  ██║ ╚████║╚██████╔╝██║  ██║██║ ╚═╝ ██║██║  ██║███████╗██║███████╗███████╗
  ╚═╝  ╚═══╝ ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝╚═╝  ╚═╝╚══════╝╚═╝╚══════╝╚══════╝

  */
  // region normalizeSearchText
  normalizeSearchText(value: string): string {

    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  }

  /*
  ████████╗ ██████╗ ██╗  ██╗███████╗███╗   ██╗███████╗
  ╚══██╔══╝██╔═══██╗██║ ██╔╝██╔════╝████╗  ██║██╔════╝
     ██║   ██║   ██║█████╔╝ █████╗  ██╔██╗ ██║███████╗
     ██║   ██║   ██║██╔═██╗ ██╔══╝  ██║╚██╗██║╚════██║
     ██║   ╚██████╔╝██║  ██╗███████╗██║ ╚████║███████║
     ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝╚═╝  ╚═══╝╚══════╝

  */
  // region buildHousesFlatsSearchTokens
  buildHousesFlatsSearchTokens(): string[] {

    return this.normalizeSearchText(this.housesFlatsSearch)
      .trim()
      .split(/\s+/)
      .filter(
        (token) => token.length > 0
      );

  }

  /*
   █████╗ ███╗   ███╗ ██████╗ ██╗   ██╗███╗   ██╗████████╗
  ██╔══██╗████╗ ████║██╔═══██╗██║   ██║████╗  ██║╚══██╔══╝
  ███████║██╔████╔██║██║   ██║██║   ██║██╔██╗ ██║   ██║
  ██╔══██║██║╚██╔╝██║██║   ██║██║   ██║██║╚██╗██║   ██║
  ██║  ██║██║ ╚═╝ ██║╚██████╔╝╚██████╔╝██║ ╚████║   ██║
  ╚═╝  ╚═╝╚═╝     ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═══╝   ╚═╝

  */
  // region formatSearchAmount
  formatSearchAmount(amount: number): string {

    return (amount ?? 0).toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );

  }

  /*
   ███████╗████████╗ █████╗ ████████╗██╗   ██╗███████╗
   ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██║   ██║██╔════╝
   ███████╗   ██║   ███████║   ██║   ██║   ██║███████╗
   ╚════██║   ██║   ██╔══██║   ██║   ██║   ██║╚════██║
   ███████║   ██║   ██║  ██║   ██║   ╚██████╔╝███████║
   ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝    ╚═════╝ ╚══════╝

  */
  // region buildHouseFlatStatusLabel
  // Replica LITERAL de las etiquetas del badge del HTML.
  // Ojo: `false` significa 'Pendiente' en payment y 'Sin pago' en movement.
  // No se usa `movement.statusDescription`: es otra cosa.
  buildHouseFlatStatusLabel(houseFlat: HouseFlat): string {

    if(houseFlat.payment) {

      if(houseFlat.payment.status === true) {
        return 'Pagado';
      }

      if(houseFlat.payment.status === false) {
        return 'Pendiente';
      }

      return 'Sin pago';

    }

    if(houseFlat.movement) {

      if(houseFlat.movement.status === true) {
        return 'Pagado';
      }

      if(houseFlat.movement.status === false) {
        return 'Sin pago';
      }

      return 'Pendiente';

    }

    return 'Sin pago';

  }

  /*
  ████████╗███████╗██╗  ██╗████████╗
  ╚══██╔══╝██╔════╝╚██╗██╔╝╚══██╔══╝
     ██║   █████╗   ╚███╔╝    ██║
     ██║   ██╔══╝   ██╔██╗    ██║
     ██║   ███████╗██╔╝ ██╗   ██║
     ╚═╝   ╚══════╝╚═╝  ╚═╝   ╚═╝

  */
  // region buildHouseFlatSearchText
  buildHouseFlatSearchText(
    houseFlat: HouseFlat,
    streetName: string
  ): string {

    const amount = houseFlat.payment?.charge
      ?? ((houseFlat.movement?.amount ?? 0) + (houseFlat.movement?.creditApplied ?? 0));

    const creditBalance = houseFlat.creditBalance ?? 0;

    const creditApplied = (houseFlat.payment?.creditApplied ?? houseFlat.movement?.creditApplied) ?? 0;

    const parts: string[] = [
      streetName ?? '',
      houseFlat.number ?? '',
      houseFlat.fullName ?? '',
      houseFlat.notes ?? '',
      houseFlat.payment?.notes ?? houseFlat.movement?.notes ?? '',
      `${amount}`,
      this.formatSearchAmount(amount),
      this.buildHouseFlatStatusLabel(houseFlat)
    ];

    if(creditBalance > 0) {

      parts.push(
        `${creditBalance}`,
        this.formatSearchAmount(creditBalance)
      );

    }

    if(creditApplied > 0) {

      parts.push(
        `${creditApplied}`,
        this.formatSearchAmount(creditApplied)
      );

    }

    return this.normalizeSearchText(
      parts.join(' ')
    );

  }

  /*
  ███╗   ███╗ █████╗ ████████╗ ██████╗██╗  ██╗███████╗███████╗
  ████╗ ████║██╔══██╗╚══██╔══╝██╔════╝██║  ██║██╔════╝██╔════╝
  ██╔████╔██║███████║   ██║   ██║     ███████║█████╗  ███████╗
  ██║╚██╔╝██║██╔══██║   ██║   ██║     ██╔══██║██╔══╝  ╚════██║
  ██║ ╚═╝ ██║██║  ██║   ██║   ╚██████╗██║  ██║███████╗███████║
  ╚═╝     ╚═╝╚═╝  ╚═╝   ╚═╝    ╚═════╝╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region matchesHouseFlatSearch
  matchesHouseFlatSearch(
    houseFlat: HouseFlat,
    streetName: string,
    tokens: string[]
  ): boolean {

    if(tokens.length === 0) {
      return true;
    }

    const text = this.buildHouseFlatSearchText(
      houseFlat,
      streetName
    );

    return tokens.every(
      (token) => text.includes(token)
    );

  }

  /*
   ██████╗ ███╗   ██╗███████╗███████╗ █████╗ ██████╗  ██████╗██╗  ██╗
  ██╔═══██╗████╗  ██║██╔════╝██╔════╝██╔══██╗██╔══██╗██╔════╝██║  ██║
  ██║   ██║██╔██╗ ██║███████╗█████╗  ███████║██████╔╝██║     ███████║
  ██║   ██║██║╚██╗██║╚════██║██╔══╝  ██╔══██║██╔══██╗██║     ██╔══██║
  ╚██████╔╝██║ ╚████║███████║███████╗██║  ██║██║  ██║╚██████╗██║  ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝

  */
  // region onHousesFlatsSearchChange
  onHousesFlatsSearchChange(value: string) {

    this.housesFlatsSearch = value ?? '';

    this.applyHousesFlatsFilter();

  }

  /*
   ██████╗██╗     ███████╗ █████╗ ██████╗
  ██╔════╝██║     ██╔════╝██╔══██╗██╔══██╗
  ██║     ██║     █████╗  ███████║██████╔╝
  ██║     ██║     ██╔══╝  ██╔══██║██╔══██╗
  ╚██████╗███████╗███████╗██║  ██║██║  ██║
   ╚═════╝╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝

  */
  // region clearHousesFlatsSearch
  clearHousesFlatsSearch(input?: HTMLInputElement) {

    this.onHousesFlatsSearchChange('');

    if(input) {
      input.focus();
    }

  }

}
